import React, { useState } from 'react'
import {
  ActivityIndicator,
  Dimensions,
  Image,
  ImageBackground,
  StyleSheet,
  Text,
  View,
} from 'react-native'

import { BottomSheet, colors, Icon, SH3, Stack } from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import { DEVICE_WIDTH } from '@zet/utils/Constants'

import { useBrandDetailsById } from '../../api/brands/hooks'
import SBMLine from '../../assets/svg/SBMLine.svg'
import CardGroup from '../../assets/svg/cardGroup'
import { sbmCrossSellImages } from '../../utils/constants'
import { BrandCardProps } from '../brandCardV2'
import WebApplyButton from '../productCard/webApplyButton'

const { height: DEVICE_HEIGHT } = Dimensions.get('window')

interface IProps {
  isSourceWeb: boolean
  customerId: number
  customerPhoneNumber: number | string
  customerName: string
  eligibleCardsCountExcludingMagnet?: number
  setIsBottomSheetVisible?: React.Dispatch<React.SetStateAction<boolean>>
  data: BrandCardProps
}

const SbmInfoCard = ({
  data,
  isSourceWeb,
  customerId,
  customerPhoneNumber,
  eligibleCardsCountExcludingMagnet,
  customerName,
}: IProps) => {
  const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false)
  const { data: magnetBenefits, isLoading } = useBrandDetailsById(193)
  const { recommendationSbmContext } = magnetBenefits || {}
  const snapPoints = [DEVICE_HEIGHT * 0.86]

  if (isLoading) {
    return (
      <Stack style={styles.centered}>
        <ActivityIndicator color={colors.primary[100]} size="large" />
      </Stack>
    )
  }

  return (
    <>
      <Pressable
        onPress={() => setIsBottomSheetVisible(true)}
        style={styles.pressableCard}
      >
        <Stack style={styles.iconWrapper}>
          <CardGroup size={24} color={colors.primary[150]} />
        </Stack>

        <Stack style={styles.textWrapper}>
          <SH3 style={styles.cardTitle}>
            Tired of loan & credit card rejections?{'\n'}
            <Text style={styles.cardHighlight}>Get SBM ZET Credit Card</Text>
          </SH3>
        </Stack>

        <Stack style={styles.arrowWrapper}>
          <Icon type="ArrowRight" color={colors.grays[70]} />
        </Stack>
      </Pressable>

      <BottomSheet
        visible={isBottomSheetVisible}
        onDismiss={() => setIsBottomSheetVisible(false)}
        snapPoints={snapPoints}
      >
        <Stack style={styles.bottomSheetContent}>
          <Stack style={styles.benefitsWrapper}>
            <Image
              source={{ uri: recommendationSbmContext?.hookLine1 }}
              style={styles.hookLineImage}
              resizeMode="contain"
            />
            <SBMLine />
            <View style={styles.benefitsGrid}>
              {recommendationSbmContext?.benefits.map((item, index: number) => (
                <View key={index} style={styles.benefitItem}>
                  <Image
                    source={{ uri: item.icon }}
                    style={styles.benefitIcon}
                  />
                  <Text style={styles.benefitText}>{item.title}</Text>
                </View>
              ))}
            </View>
          </Stack>

          <ImageBackground
            source={{ uri: recommendationSbmContext?.cardBg }}
            style={styles.bgImgContainer}
            borderRadius={20}
          >
            <View style={styles.hookImageWrapper}>
              <Image
                source={{ uri: recommendationSbmContext?.hookLine2 }}
                resizeMode="contain"
                style={styles.fullImage}
              />
            </View>

            <View style={styles.illustrationWrapper}>
              <Image
                source={{ uri: sbmCrossSellImages.sbmIllustration }}
                resizeMode="contain"
                style={styles.fullImage}
              />
            </View>

            <View style={styles.btnContainer}>
              <WebApplyButton
                index={0}
                partnerId={String(data?.partnerId)}
                alreadyApplied={Boolean(data?.leadExists)}
                isSourceWeb={isSourceWeb}
                isInEligible={!data?.eligibility?.isEligible}
                customerId={customerId}
                customerPhoneNumber={customerPhoneNumber}
                leadAdded={data?.leadExists}
                customerName={customerName}
                eligibleCardsCountExcludingMagnet={
                  eligibleCardsCountExcludingMagnet
                }
                variantId={data?.id}
                isApplySbmFromBottomSheet
                setIsBottomSheetVisible={() => setIsBottomSheetVisible(false)}
              />
            </View>
          </ImageBackground>
        </Stack>
      </BottomSheet>
    </>
  )
}

export default SbmInfoCard

const styles = StyleSheet.create({
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressableCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 8,
    borderWidth: 1,
    borderColor: '#DADADA',
    borderRadius: 8,
  },
  iconWrapper: {
    flex: 0.1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  textWrapper: {
    flex: 0.8,
    paddingHorizontal: 8,
  },
  cardTitle: {
    color: '#3E3E3E',
  },
  cardHighlight: {
    color: colors.primary[150],
  },
  arrowWrapper: {
    flex: 0.1,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  bottomSheetContent: {
    flex: 1,
    alignItems: 'center',
    margin: 12,
  },
  benefitsWrapper: {
    marginHorizontal: 8,
    marginBottom: 16,
    paddingHorizontal: 10,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#F2F2F2',
    alignItems: 'center',
  },
  hookLineImage: {
    width: '95%',
    height: 80,
    alignSelf: 'center',
  },
  benefitsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 12,
  },
  benefitItem: {
    width: '49%',
    backgroundColor: '#FDFAFF',
    borderRadius: 12,
    borderColor: '#F6EDFF',
    borderWidth: 1,
    padding: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  benefitIcon: {
    width: 32,
    height: 32,
  },
  benefitText: {
    color: '#141216',
    fontFamily: 'Graphie-Regular',
    fontSize: 13,
  },
  bgImgContainer: {
    alignItems: 'center',
    borderRadius: 20,
    overflow: 'hidden',
    width: '100%',
    borderColor: '#E2D8E7',
    borderWidth: 1,
    height: DEVICE_WIDTH,
    justifyContent: 'space-between',
  },
  hookImageWrapper: {
    width: '90%',
    height: 100,
    justifyContent: 'flex-end',
  },
  illustrationWrapper: {
    width: '80%',
    height: DEVICE_WIDTH * 0.45,
  },
  btnContainer: {
    marginBottom: 16,
    width: '92%',
  },
  fullImage: {
    width: '100%',
    height: '100%',
  },
})
